import { useCallback, useRef } from 'react';

type RunOncePerKey = (key: string | null | undefined, action: () => void) => void;

/**
 * Returns a stable callback that runs `action` at most once per distinct `key`,
 * re-arming when the key changes. The guard is ref-backed so it survives re-renders
 * and React StrictMode's double-invoked effects. `null`/`undefined` keys are ignored,
 * so a transiently missing key cannot suppress a later real invocation.
 */
export function useOncePerKey(): RunOncePerKey {
	const lastRunKey = useRef<string | null>(null);

	return useCallback((key, action) => {
		if (key === null || key === undefined) {
			return;
		}
		if (lastRunKey.current === key) {
			return;
		}
		lastRunKey.current = key;
		action();
	}, []);
}
